import React, { useCallback } from 'react';
import { TouchableOpacity } from 'react-native';

import { Colors } from 'theme/colors';
import { NakedIcon } from 'theme/icons';

import {
  CheckBoxContainer,
  CheckBox,
  OptionText,
  CheckBoxInnerContainer,
  Spacer,
  BlockContainer,
  BlockIconContainer,
  BlockCheckboxContainer,
  BlockLabelContainer,
  BlockOptionText,
  DesktopCheckBox,
} from './styles';

const CHECK_ICON_SIZE = 8;
const ICON_HEIGHT = 30;
const ICON_HEIGHT_DESKTOP = 45;
const DISABLED_OPACITY = 0.5;

const ICON_STYLE = { marginBottom: -2 };

/**
 * Renders a single selectable checkbox.
 *
 * @param props - Options props.
 */
function Option<T extends Record<string, unknown>>(props: OptionProps<T>) {
  const {
    option = {},
    onSelect,
    disabled,
    stretched,
    block,
    numChecked,
    maxSelection,
    activeColor,
    iconColor,
  } = props;
  const { checked, icon, label } = option as OptionValue<T>;
  const handleSelectOption = useCallback(() => {
    if (onSelect) {
      onSelect(option as OptionValue<T>);
    }
  }, [onSelect, option]);
  const activeColorOverride = activeColor ?? Colors.green;
  const backgroundColor = checked ? activeColorOverride : Colors.white;
  const borderColor = checked ? activeColorOverride : Colors.steel;

  const opacity = disabled && !block ? DISABLED_OPACITY : 1;
  const blockOpacity = (numChecked < maxSelection && !disabled) || checked ? 1 : 0.5;
  const currentIconColor = disabled && !block ? Colors.gallery : iconColor ?? Colors.green;
  const hasBoxShadow = numChecked < maxSelection && !checked;
  const checkBoxStyle = getElevationStyle(checked, disabled, hasBoxShadow, borderColor);
  const blockContainerDisplay = block ? ['none', 'block'] : 'none';
  const checkboxContainerDisplay = block ? ['block', 'none'] : 'block';
  const checkboxInnerDisplay = block ? ['flex', 'none'] : 'flex';
  const containerWidth = block ? ['auto', '124px'] : 'auto';
  const containerHeight = block ? ['48px', '124px'] : '48px';

  return (
    <>
      <CheckBoxContainer
        display={checkboxContainerDisplay}
        height={containerHeight}
        width={containerWidth}
        borderRadius={['40px', '35px']}
        style={checkBoxStyle}>
        <TouchableOpacity activeOpacity={1} onPress={handleSelectOption} style={{ height: '100%' }}>
          <CheckBoxInnerContainer
            paddingLeft={stretched ? '15px' : '25px'}
            display={checkboxInnerDisplay}>
            {
              //iconColor will be overridden here
              icon && (
                <NakedIcon
                  name={icon}
                  height={ICON_HEIGHT}
                  opacity={opacity}
                  color={currentIconColor}
                />
              )
            }
            <OptionText style={{ opacity }}>{label}</OptionText>
            {stretched && <Spacer />}
            <CheckBox backgroundColor={backgroundColor} borderColor={borderColor}>
              {checked && (
                <NakedIcon
                  color={Colors.white}
                  name="tick"
                  size={CHECK_ICON_SIZE}
                  style={ICON_STYLE}
                />
              )}
            </CheckBox>
          </CheckBoxInnerContainer>
        </TouchableOpacity>
      </CheckBoxContainer>
      {block && (
        <BlockContainer display={blockContainerDisplay}>
          <TouchableOpacity
            activeOpacity={1}
            onPress={handleSelectOption}
            style={{ height: '100%' }}>
            <CheckBoxContainer
              height={['48px', '124px']}
              display={['none', 'block']}
              width={['auto', '124px']}
              borderRadius={'35px'}
              style={checkBoxStyle}>
              <BlockIconContainer>
                {icon && (
                  <NakedIcon
                    name={icon}
                    height={ICON_HEIGHT_DESKTOP}
                    opacity={opacity}
                    color={currentIconColor}
                  />
                )}
              </BlockIconContainer>
              <BlockCheckboxContainer>
                {checked && (
                  <DesktopCheckBox backgroundColor={backgroundColor} borderColor={borderColor}>
                    <NakedIcon
                      color={Colors.white}
                      name="tick"
                      height={ICON_HEIGHT}
                      style={ICON_STYLE}
                    />
                  </DesktopCheckBox>
                )}
              </BlockCheckboxContainer>
            </CheckBoxContainer>
            <BlockLabelContainer>
              <BlockOptionText style={{ opacity: blockOpacity }}>{label}</BlockOptionText>
            </BlockLabelContainer>
          </TouchableOpacity>
        </BlockContainer>
      )}
    </>
  );
}
/**
 * Retrieves an elevation style based on card state properties.
 *
 * @param activeCard - Indicates if the card is currently active.
 * @param enableShadow  - If true, will enable the shadow on activation.
 * @param disabled - Indicates if the card is disabled.
 * @param hasBoxShadow - Indicates wheter the card has a box shadow or not
 */
function getElevationStyle(
  activeCard: boolean,
  disabled: boolean,
  hasBoxShadow: boolean,
  borderColor: string = Colors.green
): React.CSSProperties {
  const borderWidth = hasBoxShadow ? 0 : 1;

  return {
    backgroundColor: Colors.white,
    borderColor: activeCard ? borderColor : Colors.alto,
    borderWidth: disabled ? 1 : borderWidth,
    borderStyle: 'solid',
    boxShadow: hasBoxShadow && !disabled ? '0px 3px 8.5px 0 rgba(0, 0, 0, 0.24)' : 'none',
  };
}
Option.defaultProps = {
  checked: false,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onSelect: () => {},
  disabled: false,
};

export const SingleOption = React.memo(Option);

/* Type definitions */
export interface OptionProps<T> {
  /** Contains the option to be displayed. */
  option: OptionValue<T>;
  /** Event emitted when an option is selected. */
  onSelect(option: OptionValue<T>): void;
  /** Is true when button is disabled. */
  disabled: boolean;
  /** When true the content of the option is stretched. */
  stretched?: boolean;
  /** Flag to tell whether to show a block */
  block?: boolean;
  /** Number of checkboxes clicked */
  numChecked?: number;
  /** Maximum number of selected options */
  maxSelection?: number;
  /** Icon color with which we override the main color*/
  iconColor?: string;
  /** Active Color that will override the main primary color */
  activeColor?: string;
}

export interface OptionValue<T> {
  /** If true, renders the checkbox as selected. */
  checked?: boolean;
  /** An 'exclusive' option will prevent any other option from being selectable. */
  optionType?: 'exclusive';
  /** The text used as a user response. */
  replyText?: string;
  /** Name of the icon to be displayed. */
  icon?: string;
  /** The text to display on the option. */
  label: string;
  /** The payload of the option. */
  payload?: T;
}
